import React, { useMemo, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const toDayKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const SignupCalendarView = ({
    events,
    selectedDayKey,
    onDayClick,
    onMonthChange,
    initialMonth: initialMonthProp,
    loading,
}) => {
    const dailyCounts = useMemo(() => {
        const counts = {};
        events.forEach((e) => {
            if (!e.occurred_at) return;
            const key = toDayKey(new Date(e.occurred_at));
            counts[key] = (counts[key] || 0) + 1;
        });
        return counts;
    }, [events]);

    const stats = useMemo(() => {
        const entries = Object.entries(dailyCounts);
        if (!entries.length) return { max: 0, top: null, avg: 0, daysWithSignups: 0 };
        let max = 0;
        let top = null;
        let total = 0;
        entries.forEach(([k, v]) => {
            total += v;
            if (v > max) {
                max = v;
                top = k;
            }
        });
        return {
            max,
            top,
            avg: total / entries.length,
            daysWithSignups: entries.length,
        };
    }, [dailyCounts]);

    const [viewMonth, setViewMonth] = useState(() => {
        if (initialMonthProp) {
            const d = new Date(initialMonthProp);
            return new Date(d.getFullYear(), d.getMonth(), 1);
        }
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });

    useEffect(() => {
        if (onMonthChange) onMonthChange(viewMonth);
    }, [viewMonth, onMonthChange]);

    const monthGrid = useMemo(() => {
        const year = viewMonth.getFullYear();
        const month = viewMonth.getMonth();
        const firstOfMonth = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        // Mon = 0 ... Sun = 6
        const offset = (firstOfMonth.getDay() + 6) % 7;

        const cells = [];
        for (let i = 0; i < offset; i++) cells.push(null);
        for (let d = 1; d <= daysInMonth; d++) {
            cells.push(new Date(year, month, d));
        }
        while (cells.length % 7 !== 0) cells.push(null);
        return cells;
    }, [viewMonth]);

    const goPrev = () =>
        setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
    const goNext = () =>
        setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));

    const monthLabel = viewMonth.toLocaleString('en-AU', {
        month: 'long',
        year: 'numeric',
    });

    const intensityClass = (count) => {
        if (!count) return 'bg-gray-50 text-gray-400';
        if (!stats.max) return 'bg-gray-50 text-gray-400';
        const ratio = count / stats.max;
        if (ratio >= 0.8) return 'bg-indigo-600 text-white';
        if (ratio >= 0.6) return 'bg-indigo-500 text-white';
        if (ratio >= 0.4) return 'bg-indigo-400 text-white';
        if (ratio >= 0.2) return 'bg-indigo-300 text-indigo-900';
        return 'bg-indigo-100 text-indigo-800';
    };

    const formatTopDay = (key) => {
        if (!key) return '—';
        const [y, m, d] = key.split('-').map(Number);
        return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
        });
    };

    return (
        <div className={`max-w-3xl mx-auto transition-opacity ${loading ? 'opacity-50' : ''}`}>
            {/* Stats summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <div className="bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                    <div className="text-[11px] text-indigo-700 font-medium">Total signups</div>
                    <div className="text-base font-bold text-indigo-900">
                        {events.length.toLocaleString()}
                    </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                    <div className="text-[11px] text-emerald-700 font-medium">Top day</div>
                    <div className="text-xs font-bold text-emerald-900">
                        {formatTopDay(stats.top)}{' '}
                        <span className="text-emerald-700 font-semibold">
                            ({stats.max})
                        </span>
                    </div>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                    <div className="text-[11px] text-blue-700 font-medium">Daily avg</div>
                    <div className="text-base font-bold text-blue-900">
                        {stats.avg ? stats.avg.toFixed(1) : '0'}
                    </div>
                </div>
                <div className="bg-purple-50 border border-purple-100 rounded-lg px-3 py-2">
                    <div className="text-[11px] text-purple-700 font-medium">Active days</div>
                    <div className="text-base font-bold text-purple-900">
                        {stats.daysWithSignups}
                    </div>
                </div>
            </div>

            {/* Month nav */}
            <div className="flex items-center justify-between mb-2">
                <button
                    onClick={goPrev}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="Previous month"
                >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h3 className="text-lg font-bold text-gray-900">{monthLabel}</h3>
                <button
                    onClick={goNext}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    aria-label="Next month"
                >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
            </div>

            {/* Weekday header */}
            <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                {WEEKDAYS.map((d) => (
                    <div
                        key={d}
                        className="text-center text-xs font-semibold text-gray-500 py-1"
                    >
                        {d}
                    </div>
                ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-1.5">
                {monthGrid.map((date, idx) => {
                    if (!date) {
                        return <div key={`empty-${idx}`} className="h-14" />;
                    }
                    const key = toDayKey(date);
                    const count = dailyCounts[key] || 0;
                    const isSelected = selectedDayKey === key;
                    const today = toDayKey(new Date()) === key;

                    return (
                        <button
                            key={key}
                            onClick={() => onDayClick && onDayClick(isSelected ? null : key)}
                            className={`h-14 rounded-lg flex flex-col items-center justify-center text-xs transition-all relative cursor-pointer hover:ring-2 hover:ring-indigo-400
                                ${intensityClass(count)}
                                ${isSelected ? 'ring-2 ring-indigo-700 ring-offset-1' : ''}
                                ${today && !isSelected ? 'ring-1 ring-gray-400' : ''}
                            `}
                            title={`${date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })} — ${count} signup${count !== 1 ? 's' : ''}`}
                        >
                            <span className="font-semibold leading-none">
                                {date.getDate()}
                            </span>
                            {count > 0 && (
                                <span className="text-[10px] font-bold mt-0.5 leading-none">
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-end gap-2 mt-4 text-xs text-gray-500">
                <span>Less</span>
                <div className="w-4 h-4 rounded bg-gray-50 border border-gray-200" />
                <div className="w-4 h-4 rounded bg-indigo-100" />
                <div className="w-4 h-4 rounded bg-indigo-300" />
                <div className="w-4 h-4 rounded bg-indigo-400" />
                <div className="w-4 h-4 rounded bg-indigo-500" />
                <div className="w-4 h-4 rounded bg-indigo-600" />
                <span>More</span>
            </div>

            {selectedDayKey && (
                <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-between">
                    <div className="text-sm text-indigo-900">
                        Filtered to <span className="font-bold">{formatTopDay(selectedDayKey)}</span>{' '}
                        — {dailyCounts[selectedDayKey] || 0} signup
                        {(dailyCounts[selectedDayKey] || 0) !== 1 ? 's' : ''}
                    </div>
                    <button
                        onClick={() => onDayClick && onDayClick(null)}
                        className="text-xs font-semibold text-indigo-700 hover:text-indigo-900"
                    >
                        Clear filter
                    </button>
                </div>
            )}
        </div>
    );
};

export default SignupCalendarView;
